import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";

import { 
    form, 
    formField, 
    formFieldLabel, 
    formFieldInput, 
    avatarUpload, 
    btnPrimary,
    guideHelpRow,
    guideHelpBtn
} from "./auth-forms.module.scss";

import useUser from "../hooks/use-user";
import ImageUploader from "../../../components/molecules/image-uploader";
import { useModalContext } from "../../../contexts/modal.context";
import { EModal } from "../../../models/modal.model";
import { normalizeApiFileUrl } from "../../../utils/api-url";
import { getBase64 } from "../../../utils/file-utils";

const updateDataSchema = Yup.object({
    name: Yup.string(),
    email: Yup.string(),
});

const AccountDataForm = () => {
    const { t } = useI18next();
    const { updateProfile, data, profile } = useUser();
    const { addModal } = useModalContext();

    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState("");

    const handleOpenPhotoGuide = () => {
        addModal({
            key: EModal.PHOTO_GUIDE,
        });
    };

    const onUploadPhoto = (file: File) => {
        setUploadFile(file);
    };

    useEffect(() => {
        setImageUrl(normalizeApiFileUrl(profile?.avatarUri));
    }, [profile?.avatarUri]);

    return (
        <Formik
            initialValues={{ name: profile?.firstName || "", email: data?.email || "" }}
            validationSchema={updateDataSchema}
            enableReinitialize={true}
            onSubmit={async (values, formikHelpers) => {
                try {
                    let avatarBase64: string | undefined;

                    if (uploadFile) {
                        const uploadedAvatar = await getBase64(uploadFile, undefined, {
                            maxWidthOrHeight: 500,
                        });
                        if (typeof uploadedAvatar.content !== "string") {
                            throw new Error("Nie udało się odczytać zdjęcia profilowego.");
                        }
                        avatarBase64 = uploadedAvatar.content;
                    }

                    updateProfile({ ...values, avatarBase64 });
                    setUploadFile(null);
                } finally {
                    formikHelpers.setSubmitting(false);
                }
            }}
        >
            {(formik) => (
                <form className={form} onSubmit={formik.handleSubmit}>
                    <label className={formField}>
                        <span className={formFieldLabel}>Imię</span>
                        <input
                            type="text"
                            className={formFieldInput}
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                    </label>

                    <label className={formField}>
                        <span className={formFieldLabel}>Adres e-mail</span>
                        <input
                            type="email"
                            className={formFieldInput}
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled
                        />
                    </label>

                    <div className={formField}>
                        <span className={formFieldLabel}>Zdjęcie profilowe</span>
                        <div className={avatarUpload}>
                            <ImageUploader onUploadPhoto={onUploadPhoto} imageUrl={imageUrl} />
                        </div>
                        <div className={guideHelpRow}>
                            <button className={guideHelpBtn} type="button" onClick={handleOpenPhotoGuide}>
                                <span className="qmark">?</span>
                                Jak właściwie wybrać zdjęcie?
                            </button>
                        </div>
                    </div>

                    <button className={btnPrimary} type="submit" disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? "Zapisywanie..." : "Zapisz dane"}
                    </button>
                </form>
            )}
        </Formik>
    );
};

export default AccountDataForm;
